import { gql, useQuery } from '@apollo/client'
import { ForbiddenError } from '../components/ErrorBoundary'
import { StructParams, getChildType, QueryStructParams, StructureBase, VDCounts } from '../lib/structures'
import { activityField } from './comon-fields'
import { useEffect } from 'react'

interface ActivityAttendanceChildren {
  personCount: number
  current: {
    within: number
    outwith: number
  }
  last30days: {
    within: number
    outwith: number
  }
  last7days: {
    within: number
    outwith: number
  }
  pubRepsAttending: {
    within: number
    outwith: number
  }
}

interface ActivityAttendanceBase extends StructureBase {
  counts: VDCounts
  activity: ActivityAttendaceDataForSelf
}

interface ActivityAttendaceDataForSelf {
  personTypeAttendance: ActivityAttendanceChildren
}

interface StructureWithActivityAttendance extends ActivityAttendanceBase{
  activity: ActivityAttendaceDataForSelf
  parents: StructureBase[]
  children: ActivityAttendanceBase[]
}

interface ActivityAttendanceReturns {
  stats: StructureWithActivityAttendance | undefined
  loading: boolean
}

interface ActivityAttendanceResult {
  structure: StructureWithActivityAttendance
}

const query = gql`
  query getActivityAttendanceData(
    $type: String!
    $code: String!
    $childType: String!
    $filterLabel: String
    $filterPersonType: [String]
  ) {
    structure(type: $type, code: $code, filterLabel: $filterLabel) {
      type
      code
      name
      structureHasAssignedWards
      userHasAdminAccess
      responsiblePersons {
        firstname
        surname
        displayname
      }
      parents {
        type
        code
        name
        userHasAccess
        userHasAdminAccess
      }
      ${activityField}
      children(type: $childType) {
        type
        code
        name
        responsiblePersons {
          firstname
          surname
          displayname
        }
        userHasAccess
        structureHasAssignedWards
         ${activityField}
      }
    }
  }
`

const queryWithoutChildren = gql`
  query getActivityAttendanceData(
    $type: String!
    $code: String!
    $filterLabel: String
    $filterPersonType: [String]
  ) {
    structure(type: $type, code: $code, filterLabel: $filterLabel) {
      type
      code
      name
      structureHasAssignedWards
      userHasAdminAccess
      responsiblePersons {
        firstname
        surname
        displayname
      }
      parents {
        type
        code
        name
        userHasAccess
        userHasAdminAccess
      }
       ${activityField}
    }
  }
`

export function useActivityAttendance ({ type, code, filterLabel, filterPersonType }: StructParams): ActivityAttendanceReturns {
  const childType = getChildType({ type, code })
  const { error, data, refetch, loading } = useQuery<ActivityAttendanceResult, QueryStructParams>(
    type === 'constituency' ? queryWithoutChildren : query, { variables: { type, code, childType, filterLabel, filterPersonType }, notifyOnNetworkStatusChange: true }
  )

  useEffect(() => {
    refetch()
  }, [filterLabel, refetch, filterPersonType])

  if (error?.graphQLErrors[0]?.extensions?.code === 'FORBIDDEN') {
    throw new ForbiddenError('this structure')
  }
  if (error != null) throw error

  return { stats: data?.structure, loading: loading }
}
